import { Link } from 'react-router-dom';
import { getMcpCurlExample, getMcpEndpoint, getMcpHealthUrl, getMcpRegisterEndpoint, getMcpTokenEndpoint } from '../lib/mcp';

const CONNECTION_FACTS = [
  { label: 'Transport', value: 'Remote HTTP MCP server' },
  { label: 'Method', value: 'POST JSON-RPC requests to /mcp' },
  { label: 'Authentication', value: 'OAuth 2.1 Bearer token (PKCE flow)' },
  { label: 'Starter tool', value: 'gxp_search_firms' },
] as const;

const QUICK_START_STEPS = [
  'Register your MCP client via POST /api/oauth/register to get a client_id and registration_access_token.',
  'Implement PKCE in your client — generate a code_verifier and derive a code_challenge via SHA-256 (S256).',
  'Redirect your user to /api/oauth/authorize with your client_id, code_challenge, and scope=mcp.',
  'Exchange the authorization code for an access_token and refresh_token at /api/oauth/token.',
  'Send the access_token as Authorization: Bearer <access_token> on each MCP request.',
] as const;

export function McpSetupPage() {
  const endpoint = getMcpEndpoint();
  const healthUrl = getMcpHealthUrl();
  const curlExample = getMcpCurlExample();

  return (
    <div className="space-y-8">
      <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
        <div className="max-w-3xl">
          <h2 className="text-2xl font-bold text-slate-900">MCP Setup</h2>
          <p className="mt-2 text-sm leading-6 text-slate-600">
            Use GxP Sentinel from any MCP-compatible client by registering an OAuth client, implementing the PKCE flow to get
            an access token, and sending it as a Bearer token on each request.
          </p>
        </div>
        <Link
          to="/oauth-clients"
          className="inline-flex items-center justify-center rounded-md bg-sentinel-700 px-4 py-2 text-sm font-medium text-white hover:bg-sentinel-900"
        >
          View OAuth Clients
        </Link>
      </div>

      <div className="grid gap-6 xl:grid-cols-[1.2fr,0.8fr]">
        <section className="rounded-xl border border-slate-200 bg-white p-6 shadow-sm">
          <h3 className="text-lg font-semibold text-slate-900">Quick start</h3>
          <ol className="mt-4 space-y-4 text-sm text-slate-700">
            {QUICK_START_STEPS.map((step, index) => (
              <li key={step} className="flex gap-3">
                <span className="mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-sentinel-100 text-xs font-semibold text-sentinel-900">
                  {index + 1}
                </span>
                <span className="leading-6">{step}</span>
              </li>
            ))}
          </ol>

          <div className="mt-6 grid gap-4 md:grid-cols-2">
            <div className="rounded-lg border border-slate-200 bg-slate-50 p-4">
              <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">MCP endpoint</p>
              <code className="mt-2 block break-all text-sm text-slate-900">{endpoint}</code>
            </div>
            <div className="rounded-lg border border-slate-200 bg-slate-50 p-4">
              <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Health check</p>
              <code className="mt-2 block break-all text-sm text-slate-900">{healthUrl}</code>
            </div>
          </div>

          <div className="mt-6 rounded-lg border border-amber-200 bg-amber-50 p-4 text-sm text-amber-900">
            Your available tools and limits depend on your subscription tier. If a client connects successfully but a tool is
            rejected, check your plan entitlements before treating it as an outage.
          </div>
        </section>

        <aside className="rounded-xl border border-slate-200 bg-white p-6 shadow-sm">
          <h3 className="text-lg font-semibold text-slate-900">Connection facts</h3>
          <div className="mt-4 space-y-4 text-sm text-slate-700">
            {CONNECTION_FACTS.map((item) => (
              <div key={item.label} className="rounded-lg border border-slate-200 bg-slate-50 p-4">
                <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">{item.label}</p>
                <p className="mt-2 leading-6 text-slate-900">{item.value}</p>
              </div>
            ))}
          </div>
        </aside>
      </div>

      <section className="rounded-xl border border-slate-200 bg-white p-6 shadow-sm">
        <div className="flex flex-col gap-2 md:flex-row md:items-center md:justify-between">
          <div>
            <h3 className="text-lg font-semibold text-slate-900">Test with curl</h3>
            <p className="text-sm text-slate-600">
              This verifies your OAuth token, endpoint, and first tool call without needing client-specific setup first.
            </p>
          </div>
          <Link to="/usage" className="text-sm font-medium text-sentinel-700 hover:text-sentinel-900">
            Open Usage after testing
          </Link>
        </div>
        <pre className="mt-4 overflow-x-auto rounded-lg bg-slate-950 p-4 text-sm leading-6 text-slate-100">
          <code>{curlExample}</code>
        </pre>
      </section>

      <section className="rounded-xl border border-slate-200 bg-white p-6 shadow-sm">
        <h3 className="text-lg font-semibold text-slate-900">Client configuration checklist</h3>
        <div className="mt-4 grid gap-4 md:grid-cols-2">
          <div className="rounded-lg border border-slate-200 bg-slate-50 p-4 text-sm text-slate-700">
            <p className="font-semibold text-slate-900">Required settings</p>
            <ul className="mt-3 space-y-2 leading-6">
              <li>Server URL: use the MCP endpoint above.</li>
              <li>Auth header: send <code>Authorization: Bearer &lt;your_oauth_access_token&gt;</code>.</li>
              <li>HTTP method: POST.</li>
              <li>Payload format: JSON-RPC 2.0.</li>
              <li>Token refresh: use your refresh_token to get a new access_token before expiry.</li>
            </ul>
          </div>
          <div className="rounded-lg border border-slate-200 bg-slate-50 p-4 text-sm text-slate-700">
            <p className="font-semibold text-slate-900">Good operating practice</p>
            <ul className="mt-3 space-y-2 leading-6">
              <li>Register one OAuth client per integration or team to isolate access.</li>
              <li>Store refresh tokens securely — they are long-lived and allow token renewal.</li>
              <li>Revoke OAuth clients immediately if compromise is suspected.</li>
              <li>Confirm Usage and Recent Activity after onboarding a new client.</li>
            </ul>
          </div>
        </div>
      </section>
    </div>
  );
}